import React from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import LandingDropdown from './LandingDropdown';
import {
  LANDING_MORE_NAV_ITEMS,
  LANDING_NAV_ITEMS,
  LANDING_NAVBAR_LIGHT_TEXT_SECTION_IDS,
  LANDING_NAVBAR_ON_COLOR_SECTION_IDS
} from './landingNavItems';

interface LandingNavbarProps {
  activeSectionId: string;
  onNavigate: (sectionId: string) => void;
  className?: string;
}

const LandingNavbar: React.FC<LandingNavbarProps> = ({ activeSectionId, onNavigate, className }) => {
  const isMoreSectionActive = LANDING_MORE_NAV_ITEMS.some((item) => item.id === activeSectionId);
  const onColorSection = LANDING_NAVBAR_ON_COLOR_SECTION_IDS.includes(activeSectionId);
  const onLightTextSection = LANDING_NAVBAR_LIGHT_TEXT_SECTION_IDS.includes(activeSectionId);
  const activeItemClass = onLightTextSection ? 'gap-2 bg-[#D7D1F726]' : 'gap-2 bg-[#5F46DE26]';
  const activeDotClass = onLightTextSection ? 'bg-white' : 'bg-[#0A0A0A]';

  return (
    <nav
      className={cn(
        'p-1 w-50 rounded-[16px] flex items-center justify-center transition-colors duration-300',
        onColorSection ? 'bg-[#FFFFFF26]' : 'bg-[#1212140A]',
        className
      )}
    >
      <ul
        className={cn(
          'font-geist flex items-center justify-center transition-colors duration-300',
          onLightTextSection ? 'text-white' : 'text-[#0A0A0A]'
        )}
      >
        {LANDING_NAV_ITEMS.map((item) => {
          const isActive = activeSectionId === item.id;

          return (
            <li
              key={item.id}
              className={cn(
                'inline-flex items-center justify-center py-2 px-3.5 xl:px-4 rounded-[16px]',
                isActive && activeItemClass
              )}
            >
              {isActive && <div className={cn('size-1.5 rounded-full', activeDotClass)} aria-hidden="true" />}
              <button
                type="button"
                onClick={() => onNavigate(item.id)}
                aria-current={isActive ? 'page' : undefined}
                className="text-inherit text-sm xl:text-base"
              >
                {item.label}
              </button>
            </li>
          );
        })}

        <li>
          <LandingDropdown
            align="center"
            options={LANDING_MORE_NAV_ITEMS.map((item) => ({
              value: item.id,
              label: item.label
            }))}
            onSelect={onNavigate}
            trigger={(open) => {
              const isActive = open || isMoreSectionActive;

              return (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-haspopup="menu"
                  className={cn(
                    'inline-flex items-center justify-center gap-2 py-2 px-4 rounded-[16px] text-inherit',
                    isActive && activeItemClass
                  )}
                >
                  {isActive && <div className={cn('size-1.5 rounded-full', activeDotClass)} aria-hidden="true" />}
                  More
                  <ChevronDown className={cn('size-4 transition-transform duration-200', open && 'rotate-180')} />
                </button>
              );
            }}
          />
        </li>
      </ul>
    </nav>
  );
};

export default LandingNavbar;
